import { useCallback, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { CredentialPreflight } from '../../../components/preflight/CredentialPreflight';
import { PreflightResult } from '../../../components/preflight/types';
import { runWorkerPreflight } from './api';

const VERIFIED = 'verified';
const UNVERIFIED = '';

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(v => (typeof v === 'string' ? v.trim() : '')).filter(Boolean)
    : [];

export const TemporalWorkerPreflightField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, formData, formContext, uiSchema, rawErrors } = props;
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // Which sibling array field holds the worker object ids (defaults to workerObjectIds).
  const workersField =
    (uiSchema?.['ui:options']?.workersField as string) ?? 'workerObjectIds';
  const objectIds = useMemo(
    () =>
      asStringArray(
        (formContext?.formData as Record<string, unknown>)?.[workersField],
      ),
    [formContext, workersField],
  );
  const signature = useMemo(() => JSON.stringify(objectIds), [objectIds]);

  const onVerify = useCallback(
    () => runWorkerPreflight(discovery, fetchApi, objectIds),
    [discovery, fetchApi, objectIds],
  );
  // The field satisfies its `^verified$` schema only once every check passed,
  // blocking the wizard until every worker object id resolves to a real SP.
  const onResult = useCallback(
    (result: PreflightResult) => onChange(result.ok ? VERIFIED : UNVERIFIED),
    [onChange],
  );
  const onReset = useCallback(() => onChange(UNVERIFIED), [onChange]);

  const schemaError = (rawErrors ?? []).length > 0 && formData !== VERIFIED;

  return (
    <div
      style={{
        background: 'var(--bui-bg-neutral-1)',
        border: '1px solid var(--bui-border-1)',
        borderRadius: 'var(--bui-radius-3)',
        padding: '16px 18px',
      }}
    >
      <CredentialPreflight
        onVerify={onVerify}
        onResult={onResult}
        onReset={onReset}
        resetSignal={signature}
        title="Pre-flight worker check"
        description="Checks that every worker object id resolves to an existing Entra service principal. All checks must pass before you can continue."
        laneName="Service principals"
      />
      {schemaError && (
        <div
          style={{
            marginTop: 10,
            fontSize: 12.5,
            color: 'var(--bui-fg-danger)',
          }}
        >
          Run the verification before you can continue.
        </div>
      )}
    </div>
  );
};
